import { CompetitorReportTemplateParameters } from "@/mixins/http/ReportTemplateMixin";
import {
  AutomaticReportCommonParameters,
  AutomaticReportWithInlineTemplateParameters,
  AutomaticReportWithTemplateParameters,
} from "@/mixins/http/AutomaticReportMixin";
import {
  normalizeReportName,
  validateReportName,
} from "@/services/ReportNameHelper";
import { AutomaticReportJobStatus } from "@/models/AutomaticReports/AutomaticReportJob";
import { EMPTY } from "./emptyValue";

export type GranularityKind = "day" | "week" | "month" | "year";
export type WeekType = "mon_sun" | "sun_sat";

export const GRANULARITY_KIND_LABELS: Record<GranularityKind, string> = {
  day: "Giorni",
  week: "Settimane",
  month: "Mesi",
  year: "Anni",
};

export const WEEK_TYPE_LABELS: Record<WeekType, string> = {
  mon_sun: "Lun-Dom",
  sun_sat: "Dom-Sab",
};

export const AUTOMATIC_REPORT_JOB_STATUS_LABELS: Record<
  AutomaticReportJobStatus,
  string
> = {
  todo: "In attesa",
  processing: "In elaborazione",
  done: "Completato",
  error: "Errore",
};

export const GRANULARITY_KIND_OPTIONS = Object.entries(
  GRANULARITY_KIND_LABELS
).map(([value, label]) => ({ value: value as GranularityKind, label }));

export const WEEK_TYPE_OPTIONS = Object.entries(WEEK_TYPE_LABELS).map(
  ([value, label]) => ({ value: value as WeekType, label })
);

export const MIN_QUANTITY = 1;
export const WEEK_MAX_QUANTITY = 4;
export const YEAR_MAX_QUANTITY = 5;

export function maxQuantityForKind(kind: GranularityKind): number | null {
  if (kind === "week") return WEEK_MAX_QUANTITY;
  if (kind === "year") return YEAR_MAX_QUANTITY;
  return null; // day/month have no quantity
}

export function validateAutomaticReportName(name: string): string | null {
  if (!normalizeReportName(name))
    return "Il nome del report automatico è obbligatorio.";
  return validateReportName(name);
}

export function automaticToTemplateGranularity(
  granularityKind: GranularityKind,
  weekType?: WeekType
): string {
  if (granularityKind === "week") {
    return weekType === "sun_sat" ? "sweek" : "week";
  }
  return granularityKind;
}

export type RangeSlot = { index: number; label: string };

export type GranularityRange = [number, number];

export const DAY_FULL_NAMES = [
  "lunedì",
  "martedì",
  "mercoledì",
  "giovedì",
  "venerdì",
  "sabato",
  "domenica",
];

export const MONTH_FULL_NAMES = [
  "gennaio",
  "febbraio",
  "marzo",
  "aprile",
  "maggio",
  "giugno",
  "luglio",
  "agosto",
  "settembre",
  "ottobre",
  "novembre",
  "dicembre",
];

// The pickers show the first three letters of the full names.
const slots = (names: string[]): RangeSlot[] =>
  names.map((name, index) => ({
    index,
    label: name.slice(0, 3).replace(/^./, (c) => c.toUpperCase()),
  }));

export const DAY_SLOTS = slots(DAY_FULL_NAMES);
export const MONTH_SLOTS = slots(MONTH_FULL_NAMES);

export type GranularityDescribable = {
  granularityKind?: GranularityKind | string;
  granularityQuantity?: number | null;
  granularityRange?: number[] | null;
  weekType?: WeekType | string | null;
};

export type TimeSlot = { from: string; to: string };

export type AutomaticReportFormValue = {
  name: string;
  granularityKind: GranularityKind;
  granularityQuantity?: number;
  granularityRange?: GranularityRange;
  weekType?: WeekType;
  compare: boolean;
  comparisonStartsAt?: string;
  comparisonEndsAt?: string;
  timeSlots: boolean;
  timeSlotsFilter: TimeSlot[];
};

export const automaticReportFormInitialState =
  (): AutomaticReportFormValue => ({
    name: "",
    granularityKind: "day",
    compare: false,
    timeSlots: false,
    timeSlotsFilter: [],
  });

export function isAutomaticReportFormValid(
  automaticReportForm: AutomaticReportFormValue
): boolean {
  const {
    name,
    granularityKind: kind,
    granularityQuantity: quantity,
    granularityRange: range,
    compare,
    comparisonStartsAt,
    comparisonEndsAt,
    timeSlots,
    timeSlotsFilter,
  } = automaticReportForm;

  if (validateAutomaticReportName(name)) return false;

  if (kind === "day" || kind === "month") {
    if (!(Array.isArray(range) && range.length === 2)) return false;
  } else {
    const max = maxQuantityForKind(kind);
    if (typeof quantity !== "number" || quantity < MIN_QUANTITY) return false;
    if (max !== null && quantity > max) return false;
  }

  if (compare && (!comparisonStartsAt || !comparisonEndsAt)) return false;
  if (timeSlots && !timeSlotsFilter?.length) return false;

  return true;
}

export type AutomaticReportFormSource = {
  name?: string;
  granularityKind?: GranularityKind | string;
  granularityQuantity?: number | null;
  granularityRange?: number[] | null;
  weekType?: WeekType | string | null;
  competitor?: {
    compare?: boolean;
    comparisonStartsAt?: string | null;
    comparisonEndsAt?: string | null;
    timeSlots?: boolean;
    timeSlotsFilter?: TimeSlot[] | null;
  } | null;
};

export function automaticReportToFormValue(
  automaticReport: AutomaticReportFormSource
): AutomaticReportFormValue {
  const c = automaticReport.competitor ?? undefined;
  const value: AutomaticReportFormValue = {
    name: automaticReport.name ?? "",
    granularityKind:
      (automaticReport.granularityKind as GranularityKind) ?? "day",
    compare: c?.compare ?? false,
    timeSlots: c?.timeSlots ?? false,
    timeSlotsFilter: c?.timeSlotsFilter ?? [],
  };
  if (automaticReport.granularityQuantity != null) {
    value.granularityQuantity = automaticReport.granularityQuantity;
  }
  if (
    Array.isArray(automaticReport.granularityRange) &&
    automaticReport.granularityRange.length === 2
  ) {
    value.granularityRange = [
      automaticReport.granularityRange[0],
      automaticReport.granularityRange[1],
    ];
  }
  if (automaticReport.weekType) {
    value.weekType = automaticReport.weekType as WeekType;
  }
  if (c?.comparisonStartsAt) value.comparisonStartsAt = c.comparisonStartsAt;
  if (c?.comparisonEndsAt) value.comparisonEndsAt = c.comparisonEndsAt;
  return value;
}

export function buildAutomaticReportCompetitorAttributes(
  automaticReportForm: AutomaticReportFormValue
) {
  return {
    compare: automaticReportForm.compare,
    comparison_starts_at: automaticReportForm.compare
      ? automaticReportForm.comparisonStartsAt ?? null
      : null,
    comparison_ends_at: automaticReportForm.compare
      ? automaticReportForm.comparisonEndsAt ?? null
      : null,
    time_slots: automaticReportForm.timeSlots,
    time_slots_filter: automaticReportForm.timeSlots
      ? automaticReportForm.timeSlotsFilter
      : null,
  };
}

export type AutomaticReportParametersOptions = {
  active?: boolean;
};

function commonAutomaticReportParameters(
  automaticReportForm: AutomaticReportFormValue,
  options: AutomaticReportParametersOptions
): AutomaticReportCommonParameters {
  const competitorAttributes =
    buildAutomaticReportCompetitorAttributes(automaticReportForm);

  return {
    name: normalizeReportName(automaticReportForm.name),
    kind: "competitor" as const,
    active: options.active ?? true,
    granularity_kind: automaticReportForm.granularityKind,
    granularity_quantity: automaticReportForm.granularityQuantity ?? null,
    granularity_range: automaticReportForm.granularityRange ?? null,
    week_type: automaticReportForm.weekType ?? null,
    competitor_attributes: competitorAttributes,
  };
}

export function buildAutomaticReportParametersForTemplate(
  automaticReportForm: AutomaticReportFormValue,
  reportTemplateId: number,
  options: AutomaticReportParametersOptions = {}
): AutomaticReportWithTemplateParameters {
  return {
    ...commonAutomaticReportParameters(automaticReportForm, options),
    report_template_id: reportTemplateId,
  };
}

export function buildAutomaticReportParametersWithInlineTemplate(
  automaticReportForm: AutomaticReportFormValue,
  templateCompetitorAttributes: CompetitorReportTemplateParameters,
  options: AutomaticReportParametersOptions & {
    publicTemplateName?: string | null;
  } = {}
): AutomaticReportWithInlineTemplateParameters {
  const publicName = options.publicTemplateName ?? null;

  return {
    ...commonAutomaticReportParameters(automaticReportForm, options),
    template_competitor_attributes: templateCompetitorAttributes,
    save_as_public_template: publicName != null,
    public_template_name: publicName ?? undefined,
  };
}

/**
 * Indices covered by an inclusive [head, tail] range, walking FORWARD from head
 * and wrapping modulo `base`. Wrap-around ranges are valid:
 *   rangeIndices(0, 4, 7)  -> [0, 1, 2, 3, 4]           (Mon..Fri)
 *   rangeIndices(5, 3, 7)  -> [5, 6, 0, 1, 2, 3]        (Sat..next Tue, wraps)
 *   rangeIndices(10, 1, 12) -> [10, 11, 0, 1]           (Nov..Feb, wraps)
 *   rangeIndices(2, 2, 7)  -> [2]                        (single unit)
 */
export function rangeIndices(
  head: number,
  tail: number,
  base: number
): number[] {
  const out: number[] = [];
  let i = head;
  // Guard: at most `base` steps so a bad tail can never loop forever.
  for (let step = 0; step < base; step++) {
    out.push(i);
    if (i === tail) break;
    i = (i + 1) % base;
  }
  return out;
}

const GRANULARITY_UNITS: Record<GranularityKind, [string, string]> = {
  day: ["giorno", "giorni"],
  week: ["settimana", "settimane"],
  month: ["mese", "mesi"],
  year: ["anno", "anni"],
};

const unitFor = (kind: GranularityKind, count: number) =>
  GRANULARITY_UNITS[kind][count === 1 ? 0 : 1];

export function describeGranularity(
  automaticReport: GranularityDescribable
): string {
  const kind = automaticReport.granularityKind;

  if (kind === "day" || kind === "month") {
    const range = automaticReport.granularityRange;
    if (!(Array.isArray(range) && range.length === 2)) return EMPTY;
    const names = kind === "day" ? DAY_FULL_NAMES : MONTH_FULL_NAMES;
    const count = rangeIndices(range[0], range[1], names.length).length;
    return `${count} ${unitFor(kind, count)} (da ${names[range[0]] ?? EMPTY})`;
  }

  if (kind === "week" || kind === "year") {
    const quantity = automaticReport.granularityQuantity ?? 0;
    const text = `${quantity} ${unitFor(kind, quantity)}`;
    const weekTypeLabel =
      kind === "week" && automaticReport.weekType
        ? WEEK_TYPE_LABELS[automaticReport.weekType as WeekType] ??
          automaticReport.weekType
        : "";
    return weekTypeLabel ? `${text} (${weekTypeLabel})` : text;
  }

  return EMPTY;
}

export function describeAutomaticReportLink(
  automaticReport: GranularityDescribable & { name?: string }
): string {
  const name = automaticReport.name ?? EMPTY;
  const granularity = describeGranularity(automaticReport);
  return granularity === EMPTY ? name : `${name}: ${granularity}`;
}
